import type { JSX } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { Bell } from 'lucide-react';

import { useToast } from '@admin-platform/ui/components';

import { useIsTenantAdmin } from '../../hooks/useIsTenantAdmin';
import { useTenantFeatures } from '../../hooks/useTenantFeatures';
import { NotificationCenterKind, NotificationSource } from '../../graphql/enums.graphql';
import { TenantFeature } from '../../types/tenant.types';
import { Alert, AlertDescription } from '../Alert/Alert';
import { Button } from '../Button/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../Card/Card';
import { CustomSwitch } from '../CustomSwitch/CustomSwitch';
import { Skeleton } from '../Skeleton/Skeleton';

import { SOURCE_LABEL } from './CenterNotificationRow';
import { EmailNotificationScopeSelect } from './EmailNotificationScopeSelect';
import styles from './NotificationCenterPreferences.module.css';
import {
  NOTIFICATION_CENTER_PREFERENCES_QUERY,
  UPDATE_NOTIFICATION_CENTER_PREFERENCES_MUTATION,
} from './notificationCenterOps';

/** What each kind is called and when it fires, grouped the way the bell labels them. */
export const PREFERENCE_GROUPS: {
  source: NotificationSource;
  rows: {
    kind: NotificationCenterKind;
    label: string;
    hint: string;
    /** Shown only when the workspace has this feature: it can never fire otherwise. */
    feature?: TenantFeature;
    /** Sent to workspace admins only, so hidden from everyone else. */
    adminOnly?: boolean;
  }[];
}[] = [
  {
    source: NotificationSource.Forms,
    rows: [
      {
        kind: NotificationCenterKind.FormSubmission,
        label: 'New form submission',
        hint: 'A form you are set to be notified about receives an answer.',
      },
      {
        kind: NotificationCenterKind.FormActionsFailed,
        label: 'Form steps failed',
        hint: 'A step on a submission to a form you created could not run.',
      },
    ],
  },
  {
    source: NotificationSource.Okr,
    rows: [
      {
        kind: NotificationCenterKind.OkrObjectiveAssigned,
        label: 'Objective assigned to you',
        hint: 'Someone makes you the owner of an objective.',
      },
      {
        kind: NotificationCenterKind.OkrKeyResultAssigned,
        label: 'Key result assigned to you',
        hint: 'Someone makes you the owner of a key result.',
      },
      {
        kind: NotificationCenterKind.OkrTargetChanged,
        label: 'Target changed',
        hint: 'Someone else changes the target on a key result you own.',
      },
      {
        kind: NotificationCenterKind.OkrScoringOpened,
        label: 'Scoring opened',
        hint: 'A cycle opens for scoring and you have key results to score.',
      },
    ],
  },
  {
    source: NotificationSource.Email,
    rows: [
      {
        kind: NotificationCenterKind.EmailReply,
        label: 'New email',
        hint: 'Which incoming email reaches your bell: none, clients, prospects, both, or any email.',
      },
      {
        kind: NotificationCenterKind.EmailSendFailed,
        label: 'Email could not be sent',
        hint: 'A message you sent did not go out.',
      },
      {
        kind: NotificationCenterKind.InboxDigest,
        label: 'Daily inbox digest',
        hint: 'Your inbox summary for the day, when you turn on the digest in Inbox settings.',
        feature: TenantFeature.WORK_INBOX,
      },
    ],
  },
  {
    source: NotificationSource.Crm,
    rows: [
      {
        kind: NotificationCenterKind.CrmMeetingBooked,
        label: 'Meeting booked',
        hint: 'Someone books a meeting with you through Calendly.',
      },
    ],
  },
  {
    source: NotificationSource.Billing,
    rows: [
      {
        kind: NotificationCenterKind.InvoiceOverdue,
        label: 'Invoice overdue',
        hint: 'An invoice passes its due date unpaid. Sent to workspace admins.',
      },
      {
        kind: NotificationCenterKind.UsageZeroPending,
        label: 'Zero usage about to bill',
        hint: 'A metered period reported by the integration closes at zero and will bill zero within a day unless someone acts. Sent to workspace admins.',
      },
    ],
  },
  {
    source: NotificationSource.Checkins,
    rows: [
      {
        kind: NotificationCenterKind.CheckinSlackPaused,
        label: 'Check-ins paused',
        hint: 'Slack was disconnected and your check-ins were paused. Sent to workspace admins.',
      },
      {
        kind: NotificationCenterKind.CheckinReportFailed,
        label: 'Check-in report not posted',
        hint: 'A report could not be posted to its channel after several tries. Sent to workspace admins.',
      },
    ],
  },
  {
    source: NotificationSource.Integrations,
    rows: [
      {
        kind: NotificationCenterKind.DriveFoldersPaused,
        feature: TenantFeature.DRIVE_FOLDERS,
        adminOnly: true,
        label: 'Drive client folders paused',
        hint: 'Google Drive was disconnected or lost write access, so client folders stopped syncing. Sent to workspace admins.',
      },
      {
        kind: NotificationCenterKind.ImportReviewReady,
        label: 'Invoice import ready for review',
        hint: 'An invoice import finished with invoices waiting to be checked. Sent to the person who started it; imports from client folders go to workspace admins.',
      },
    ],
  },
  {
    source: NotificationSource.Tasks,
    rows: [
      {
        kind: NotificationCenterKind.TaskReminder,
        label: 'Reminder due',
        hint: 'A reminder on a task assigned to you falls due, including a snoozed task.',
      },
    ],
  },
];

/**
 * Per-kind switches for the notification center's bell. Each switch saves on
 * its own: a single boolean has nothing to review, and a Save button over
 * a dozen of them only gets in the way. Support's own table (in-app and email
 * per ticket event) is on the Support notifications page.
 */
export function NotificationCenterPreferences(): JSX.Element {
  const toast = useToast();
  // The Check-ins kinds only ever go to admins of a workspace that has the
  // feature; a switch that can never fire for this person is just noise.
  const isAdmin = useIsTenantAdmin();
  const { hasFeature } = useTenantFeatures();
  const showCheckins = isAdmin && hasFeature(TenantFeature.CHECKINS);
  const { data, loading, error, refetch } = useQuery(NOTIFICATION_CENTER_PREFERENCES_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const [save, saveState] = useMutation(UPDATE_NOTIFICATION_CENTER_PREFERENCES_MUTATION);

  const enabled = new Map(
    (data?.notificationCenterPreferences ?? []).map((p) => [p.kind, p.inApp]),
  );

  const toggle = (kind: NotificationCenterKind, inApp: boolean) => {
    void save({
      variables: { input: [{ kind, inApp }] },
      optimisticResponse: {
        updateNotificationCenterPreferences: (data?.notificationCenterPreferences ?? []).map(
          (p) => ({ ...p, inApp: p.kind === kind ? inApp : p.inApp }),
        ),
      },
      update(cache, result) {
        const next = result.data?.updateNotificationCenterPreferences;
        if (next) {
          cache.writeQuery({
            query: NOTIFICATION_CENTER_PREFERENCES_QUERY,
            data: { notificationCenterPreferences: next },
          });
        }
      },
    }).catch(() => toast('Could not save that change. Try again.'));
  };

  return (
    <Card>
      <CardHeader>
        <div className={styles.Header}>
          <span className={styles.HeaderIcon} aria-hidden="true">
            <Bell />
          </span>
          <div className={styles.HeaderText}>
            <CardTitle>Notification bell</CardTitle>
            <CardDescription>
              Choose which events show up in the bell at the top of the page.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className={styles.Content}>
        {error && !data ? (
          <Alert variant="destructive">
            <AlertDescription>
              Preferences could not be loaded.{' '}
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        ) : loading && !data ? (
          <div className={styles.Loading} aria-busy="true">
            <Skeleton height="2.5rem" />
            <Skeleton height="2.5rem" />
            <Skeleton height="2.5rem" />
          </div>
        ) : (
          PREFERENCE_GROUPS.filter(
            ({ source }) => source !== NotificationSource.Checkins || showCheckins,
          ).map(({ source, rows }) => (
            <section key={source} className={styles.Group} aria-label={SOURCE_LABEL[source]}>
              <h3 className={styles.GroupTitle}>{SOURCE_LABEL[source]}</h3>
              {rows
                .filter(
                  ({ feature, adminOnly }) =>
                    (!feature || hasFeature(feature)) && (!adminOnly || isAdmin),
                )
                .map(({ kind, label, hint }) => (
                  <div key={kind} className={styles.Row}>
                    <span className={styles.RowText}>
                      <span className={styles.RowLabel}>{label}</span>
                      <span className={styles.RowHint}>{hint}</span>
                    </span>
                    {kind === NotificationCenterKind.EmailReply ? (
                      // Not on/off but which senders; "Off" is this kind switched off.
                      <EmailNotificationScopeSelect
                        id={`center-pref-${kind}`}
                        className={styles.ScopeSelect}
                      />
                    ) : (
                      <CustomSwitch
                        id={`center-pref-${kind}`}
                        label={`${label} in the bell`}
                        hideLabel
                        checked={enabled.get(kind) ?? true}
                        disabled={saveState.loading}
                        onChange={(value) => toggle(kind, value)}
                      />
                    )}
                  </div>
                ))}
            </section>
          ))
        )}
      </CardContent>
    </Card>
  );
}
